import { useQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { MyProfile, MyProfileQueryData } from '@skippay/core/@types/myProfile';
import { bannersSelector } from '@skippay/core/modules/banners';
import { formatPhoneNumber } from '@skippay/core/utils/apiUtils';

import MyProfileQuery from 'queries/MyProfileQuery.graphql';
import { getPrimaryEmail, getPrimaryPhone } from 'utils/currentUserUtils';

import { createSelfCareContextAdapter } from './adapters/selfCareAdapter';
import { JarabotCustomerContext } from './JarabotCustomerContext';
import { JarabotIdentity, JarabotSurface } from './types';
import { useJarabotFeatureIsEnabled } from '../featureFlag';
import { useIsLoggedInSession } from '../useIsLoggedInSession';

type PrimaryEmail = MyProfile['emails'][number] | undefined;

function LoggedInJarabotCustomerContext(): JSX.Element | null {
	const isFeatureEnabled = useJarabotFeatureIsEnabled();
	const banners = useSelector(bannersSelector);

	const { data } = useQuery<MyProfileQueryData>(MyProfileQuery, {
		fetchPolicy: 'cache-first',
		skip: !isFeatureEnabled,
	});
	const myProfile = data?.myProfile;

	const adapter = useMemo(() => createSelfCareContextAdapter(() => banners), [banners]);

	const identity = useMemo<JarabotIdentity | undefined>(
		() =>
			myProfile && {
				userId: myProfile.id,
				name: myProfile.name.firstName,
				email: (getPrimaryEmail(myProfile) as PrimaryEmail)?.address,
				phone: formatPhoneNumber(getPrimaryPhone(myProfile)) || undefined,
				countryCode: myProfile.country,
				kycIsOutdated: myProfile.kyc?.isOutdated,
				amlLegalReviewRequested: myProfile.amlLegalReview?.isRequested,
			},
		[myProfile]
	);

	if (!isFeatureEnabled || !myProfile || !identity) return null;

	return (
		<JarabotCustomerContext
			surface={JarabotSurface.SELFCARE}
			adapter={adapter}
			country={myProfile.country}
			identity={identity}
		/>
	);
}

export function SelfCareJarabotCustomerContext(): JSX.Element | null {
	const isLoggedInSession = useIsLoggedInSession();

	if (!isLoggedInSession) return null;

	return <LoggedInJarabotCustomerContext />;
}
